import { themeVar } from '../tenantTheme';


/**
 * The `var()` reference Chakra itself emits for a scale entry (`gray.300` -> `--chakra-colors-gray-300`).
 *
 * Semantic tokens hold *scale references*, not CSS values: Chakra resolves `'gray.300'` through
 * `__cssMap` into `var(--chakra-colors-gray-300)`. A raw CSS `var()` fallback cannot be `gray.300`
 * (that is a token path, not a colour), so nest Chakra's own variable instead of hardcoding a hex.
 * Nested `var()` fallbacks are valid CSS and preserve the scale link, so re-pointing `gray.300`
 * still moves every semantic token that references it.
 */
const chakraColor = (token: string) => `var(--chakra-colors-${token.replace(/\./g, '-')})`;

// Only the `default` branches carry `--peli-semantic-*`. `_dark` is deliberately left alone: no
// colour mode is configured (see the CSS-variable constraints note in ../index.ts), so those
// branches are dead, and a tenant override is a single-value choice, not a light/dark pair.
export default {
  colors: {
    'bg.surface': {
      default: themeVar('semantic.bgSurface', chakraColor('white')),
      _dark: 'gray.900',
    },
    'bg.default': {
      default: themeVar('semantic.bgDefault', chakraColor('gray.50')),
      _dark: 'gray.800',
    },
    'bg.subtle': {
      default: themeVar('semantic.bgSubtle', chakraColor('gray.100')),
      _dark: 'gray.700',
    },

    // The inverse surface: dark background with light text, used by tooltips, the selected radio
    // card and LabeledValue. Was an untokenised `gray.800`/`white` pair at five sites.
    'bg.inverse': {
      default: themeVar('semantic.bgInverse', chakraColor('gray.800')),
      _dark: 'gray.100',
    },
    'text.inverse': {
      default: themeVar('semantic.textInverse', 'white'),
      _dark: 'gray.900',
    },

    // Borders
    'border.light': {
      default: themeVar('semantic.borderLight', chakraColor('gray.200')),
      _dark: 'gray.700',
    },
    'border.default': {
      default: themeVar('semantic.borderDefault', chakraColor('gray.300')),
      _dark: 'gray.600',
    },
    'border.hover': {
      default: themeVar('semantic.borderHover', chakraColor('gray.600')),
      _dark: 'gray.200',
    },
    'border.focused': {
      default: themeVar('semantic.borderFocused', chakraColor('gray.700')),
      _dark: 'gray.300',
    },

    // States
    success: {
      default: themeVar('semantic.success', chakraColor('green.500')),
      _dark: 'green.200',
    },
    info: {
      default: themeVar('semantic.info', chakraColor('blue.500')),
      _dark: 'blue.200',
    },
    warning: {
      default: themeVar('semantic.warning', chakraColor('orange.500')),
      _dark: 'orange.200',
    },
    error: {
      default: themeVar('semantic.error', chakraColor('red.500')),
      _dark: 'red.200',
    },

    // Text
    'text.default': {
      default: themeVar('semantic.textDefault', chakraColor('gray.800')),
      _dark: 'white',
    },
    'text.paragraph': {
      default: themeVar('semantic.textParagraph', chakraColor('gray.700')),
      _dark: 'white',
    },
    'text.subtle': {
      default: themeVar('semantic.textSubtle', chakraColor('gray.600')),
      _dark: 'gray.300',
    },
    'text.muted': {
      default: themeVar('semantic.textMuted', chakraColor('gray.500')),
      _dark: 'gray.400',
    },
    'text.placeholder': {
      default: themeVar('semantic.textPlaceholder', chakraColor('gray.400')),
      _dark: 'gray.200',
    },

    // Throw Away
    offBlack: {
      default: '#0e1012',
      _dark: 'gray.100',
    },
  },
  shadows: {
    xs: {
      default: '0px 0px 0px 1px rgba(0, 0, 0, 0.05)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 1px 2px rgba(13, 14, 20, 0.9)',
    },
    sm: {
      default: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 2px 4px rgba(13, 14, 20, 0.9)',
    },
    base: {
      default: '0px 1px 3px 0px rgba(0, 0, 0, 0.10), 0px 1px 2px 0px rgba(0, 0, 0, 0.06)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 4px 8px rgba(13, 14, 20, 0.9)',
    },
    md: {
      default: '0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 4px 8px rgba(13, 14, 20, 0.9)',
    },
    lg: {
      default: '0px 10px 15px -3px rgba(0, 0, 0, 0.10), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 8px 16px rgba(13, 14, 20, 0.9)',
    },
    xl: {
      default: '0px 20px 25px -5px rgba(0, 0, 0, 0.10), 0px 10px 10px -5px rgba(0, 0, 0, 0.04)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 16px 24px rgba(13, 14, 20, 0.9)',
    },
    '2xl': {
      default: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 16px 24px rgba(13, 14, 20, 0.9)',
    },
    '3xl': {
      default: '0px 25px 50px -12px rgba(0, 0, 0, 0.25)',
      _dark: '0px 0px 1px rgba(13, 14, 20, 1), 0px 16px 24px rgba(13, 14, 20, 0.9)',
    },
    focus: {
      default: '0 0 0 4px #EDF2F7',
      _dark: '0 0 0 4px #2D3748',
    },
    // The ring drawn around a focused SimpleField. Previously a literal in SimpleField's JSX;
    // a token so it can be re-pointed without touching the component. Distinct from `focus`
    // above, which is a different (unused-by-fields) treatment.
    focusRing: {
      default: themeVar('field.focusRing', '0 0 0 3px rgba(66, 153, 225, 0.6)'),
      _dark: '0 0 0 3px rgba(66, 153, 225, 0.6)',
    },
  },
};
